import * as THREE from 'three'
import type { ModelAssetFile, ModelAssetNode } from '../assets/model-asset-types'
import {
  ImportedModelObjectMetadataService,
  type ImportedModelObjectMetadata,
  type ImportedModelSelectionTargetMetadata,
} from '../../imported-model/metadata'
import {
  collectAssemblyNodeIdentityLookupIds,
  normalizeAssemblyNodeId,
} from '../../assembly/utils/AssemblyNodePath'
import {
  cloneGlbHierarchyUserDataMeta,
  type GlbHierarchyNode,
  type GlbHierarchyNodeMeta,
  type ImportedBodyEntry,
} from '../../imported-model/loaders/glb'
import {
  isRuntimeImportedBodyEntryLabel,
  mergeBodyEntriesById,
  parseImportedBodyEntries,
} from '../../imported-model/loaders/body-entries'
import {
  rebaseImportedBodyOccurrenceBindingId,
  resolveImportedBodyDefinitionId,
} from '../../imported-model/utils'
import {
  completeImportedModelTopologySingleOccurrenceRebindings,
  createImportedModelTopologyOccurrenceRebindings,
  transferImportedModelTopologySubtreeOwnership,
} from '../../imported-model/topology/imported-model-topology-runtime'
import type { FileInfo } from '../../imported-model/loaders/file-info'
import type {
  AssemblyManifest,
  AssemblyModelAsset,
  AssemblyNode,
  ComposedAssemblyResult,
  ImportedModelResult,
  ImportedTopologySource,
  Matrix4Tuple,
  PartAsset,
  SceneBindingIndex,
  SelectionIndex,
} from '../../imported-model/loaders/types'
import {
  createEmbeddedPuppyOccurrenceClaims,
  type EmbeddedPuppyOccurrenceClaim,
} from './embedded-puppy-occurrence-claim'
import {
  createProjectChildIdentityLookupSetsForPackage,
  filterPackageHierarchyChildrenRepresentedByProjectChildren,
  markPackageHierarchyChildrenRepresentedByProjectChildren,
} from './project-model-package-representation'
import {
  createProjectModelRuntimeAssemblyNodeIds,
  resolvePackageHierarchyRuntimeAssemblyNodeId as resolveScopedPackageRuntimeAssemblyNodeId,
  resolveProjectModelNodeAssemblyNodeId,
} from './project-model-node-identity'
import { rebaseProjectModelImportedTopologySources } from './rebase-project-model-imported-topology-sources'
import { completeProjectModelAssemblyTopologyBodyOccurrenceRebindings } from './create-scoped-project-model-body-occurrence-rebindings'
import { ImportedModelResourceScope } from '../../imported-model/resources/ImportedModelResourceScope'

const IMPORTED_ASSEMBLY_ROOT_NAME = 'imported-assembly'
const PROJECT_MODEL_FILE_KIND = 'project-model'
const IDENTITY_MATRIX_TUPLE: Matrix4Tuple = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
]

interface ProjectNodePackageSemanticMeta {
  readonly bodyEntries: ImportedBodyEntry[]
  readonly bodyPrimitiveIndicesById?: NonNullable<GlbHierarchyNodeMeta['bodyPrimitiveIndicesById']>
  readonly bodyHighlightTargetsById?: NonNullable<GlbHierarchyNodeMeta['bodyHighlightTargetsById']>
  readonly materialNames: string[]
  readonly materialName?: string
}

/**
 * 单个 OpenProject 节点包的解析结果。
 */
export interface ProjectModelPackageResult {
  /**
   * 包文件所属的项目模型节点 key。
   */
  readonly nodeKey: string
  /**
   * 已解析的节点包文件。
   */
  readonly file: ModelAssetFile
  /**
   * 既有 imported-model loader 产出的可挂载结果。
   */
  readonly result: ImportedModelResult
}

/**
 * 项目模型整树组合输入。
 */
export interface ComposeProjectModelImportedResultInput {
  /**
   * OpenProject 映射后的项目模型根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 已完成解析的节点包结果。
   */
  readonly packageResults?: readonly ProjectModelPackageResult[]
  /**
   * 按节点 key 索引的已加载结果。用于调用方已经按节点维度加载完成的场景。
   */
  readonly loadedResultByNodeKey?: ReadonlyMap<string, ImportedModelResult>
  /**
   * 根 tab 使用的虚拟文件描述。未传入时由根节点 key 派生。
   */
  readonly fileInfo?: FileInfo
  /**
   * 根 tab 展示名。未传入时从根节点 key 派生。
   */
  readonly partName?: string
}

/**
 * 把节点 key 的最后一段转成可读名称。
 *
 * @param nodeKey 项目模型节点 key。
 * @returns 去掉兄弟序号后的节点展示名。
 */
function resolveNodeNameFromKey(nodeKey: string): string {
  const segment = nodeKey.split('/').filter(Boolean).at(-1) ?? nodeKey
  const decodedSegment = decodeURIComponent(segment)

  return decodedSegment.trim() || nodeKey
}

/**
 * 获取项目模型节点展示名。
 *
 * @param node 项目模型节点。
 * @returns 节点展示名。
 */
function resolveNodeDisplayName(node: ModelAssetNode): string {
  return node.displayName?.trim() || resolveNodeNameFromKey(node.nodeKey)
}

/**
 * 解析项目节点语义元数据使用的源文件 ID。
 *
 * @param node 当前项目模型节点。
 * @returns 独立版本文件、内嵌容器或装配清单的稳定文件 ID。
 */
function resolveProjectNodeSemanticFileId(node: ModelAssetNode): string {
  return node.packageFiles[0]?.fileId
    ?? (node.assetSource?.kind === 'embedded-puppy-occurrence'
      ? node.assetSource.containerFile.fileId
      : undefined)
    ?? node.assemblyManifest?.fileId
    ?? node.nodeKey
}

/**
 * 将 OpenProject 返回的行主序 4x4 矩阵应用到 Three.js 对象。
 *
 * 设计说明：OpenProject 示例中平移量位于数组下标 3/7/11，说明后端展开的是行主序矩阵。
 * Three.js `Matrix4.fromArray` 按列主序解释数组，直接使用会丢失平移，因此这里必须使用
 * `Matrix4.set(...)` 按行主序参数写入。
 *
 * @param target 需要应用局部变换的场景对象。
 * @param elements OpenProject 节点 transform。
 */
function applyOpenProjectRowMajorTransform(target: THREE.Object3D, elements: readonly number[]): void {
  const source = elements.length === 16 ? elements : IDENTITY_MATRIX_TUPLE
  const matrix = new THREE.Matrix4()
  matrix.set(
    source[0], source[1], source[2], source[3],
    source[4], source[5], source[6], source[7],
    source[8], source[9], source[10], source[11],
    source[12], source[13], source[14], source[15],
  )
  matrix.decompose(target.position, target.quaternion, target.scale)
}

/**
 * 把只读矩阵数组收口为 manifest 可保存的 16 元组。
 *
 * @param elements 节点局部矩阵。
 * @returns manifest 使用的矩阵元组。
 */
function toMatrixTuple(elements: readonly number[]): Matrix4Tuple {
  const source = elements.length === 16 ? elements : IDENTITY_MATRIX_TUPLE

  return [
    source[0], source[1], source[2], source[3],
    source[4], source[5], source[6], source[7],
    source[8], source[9], source[10], source[11],
    source[12], source[13], source[14], source[15],
  ]
}

/**
 * 生成项目根 tab 对应的虚拟文件描述。
 *
 * @param root 项目模型根节点。
 * @returns imported-model 结果使用的文件描述。
 */
function createProjectModelRootFileInfo(root: ModelAssetNode): FileInfo {
  return {
    fileId: root.nodeKey,
    uuid: root.nodeKey,
    kind: PROJECT_MODEL_FILE_KIND,
    size: BigInt(0),
  }
}

/**
 * 读取项目模型节点的运行时装配身份。
 *
 * @param node 当前项目模型节点。
 * @param runtimeAssemblyNodeIds 组合前生成的运行时身份表。
 * @returns 当前节点写入 Object3D metadata 时使用的装配节点 ID。
 */
function resolveProjectModelRuntimeAssemblyNodeId(
  node: ModelAssetNode,
  runtimeAssemblyNodeIds: ReadonlyMap<ModelAssetNode, string>,
): string {
  return runtimeAssemblyNodeIds.get(node) ?? resolveProjectModelNodeAssemblyNodeId(node)
}

/**
 * 获取导入结果持有的场景根对象。
 *
 * @param result imported-model 解析结果。
 * @returns 可挂入项目模型节点容器的 Three.js 对象。
 */
function resolveImportedResultRootObject(result: ImportedModelResult): THREE.Object3D {
  return result.kind === 'composed-assembly' ? result.root : result.object
}

/**
 * 获取导入结果的权威 hierarchy。
 *
 * @param result imported-model 解析结果。
 * @returns 可合并进项目模型 hierarchy 的根节点。
 */
function resolveImportedResultHierarchy(result: ImportedModelResult): GlbHierarchyNode {
  return result.hierarchy
}

/**
 * 按 uuid 建立场景对象索引，用于把重映射后的 hierarchy 写回 metadata。
 *
 * @param root 场景根对象。
 * @returns object.uuid 到 Object3D 的映射。
 */
function buildObjectIndex(root: THREE.Object3D): ReadonlyMap<string, THREE.Object3D> {
  const objectByUuid = new Map<string, THREE.Object3D>()
  root.traverse((object) => {
    objectByUuid.set(object.uuid, object)
  })

  return objectByUuid
}

/**
 * 克隆实体到 primitive 下标的映射，避免组合结果复用子包可变引用。
 *
 * @param source 原始实体 primitive 映射。
 * @returns 可安全写入项目节点 meta 的克隆映射。
 */
function cloneBodyPrimitiveIndicesById(
  source: GlbHierarchyNodeMeta['bodyPrimitiveIndicesById'],
): NonNullable<GlbHierarchyNodeMeta['bodyPrimitiveIndicesById']> | undefined {
  if (!source) {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(source).map(([bodyId, primitiveIndices]) => [
      bodyId,
      [...primitiveIndices],
    ]),
  )
}

/**
 * 克隆实体高亮目标映射，保持设计树 hover/select 使用独立数据。
 *
 * @param source 原始实体高亮目标映射。
 * @returns 可安全写入项目节点 meta 的克隆映射。
 */
function cloneBodyHighlightTargetsById(
  source: GlbHierarchyNodeMeta['bodyHighlightTargetsById'],
): NonNullable<GlbHierarchyNodeMeta['bodyHighlightTargetsById']> | undefined {
  if (!source) {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(source).map(([bodyId, targets]) => [
      bodyId,
      targets.map((target) => ({
        objectId: target.objectId,
        primitiveIndices: target.primitiveIndices ? [...target.primitiveIndices] : undefined,
      })),
    ]),
  )
}

/**
 * 合并多个实体 primitive 映射，保留第一个命中的稳定来源。
 *
 * @param sources 待合并的实体 primitive 映射。
 * @returns 合并后的映射；没有任何条目时返回 undefined。
 */
function mergeBodyPrimitiveIndicesById(
  ...sources: Array<GlbHierarchyNodeMeta['bodyPrimitiveIndicesById']>
): NonNullable<GlbHierarchyNodeMeta['bodyPrimitiveIndicesById']> | undefined {
  const merged: NonNullable<GlbHierarchyNodeMeta['bodyPrimitiveIndicesById']> = {}

  for (const source of sources) {
    for (const [bodyId, primitiveIndices] of Object.entries(source ?? {})) {
      if (!merged[bodyId]) {
        merged[bodyId] = [...primitiveIndices]
      }
    }
  }

  return Object.keys(merged).length > 0 ? merged : undefined
}

/**
 * 合并多个实体高亮目标映射，保留第一个命中的稳定来源。
 *
 * @param sources 待合并的实体高亮目标映射。
 * @returns 合并后的映射；没有任何条目时返回 undefined。
 */
function mergeBodyHighlightTargetsById(
  ...sources: Array<GlbHierarchyNodeMeta['bodyHighlightTargetsById']>
): NonNullable<GlbHierarchyNodeMeta['bodyHighlightTargetsById']> | undefined {
  const merged: NonNullable<GlbHierarchyNodeMeta['bodyHighlightTargetsById']> = {}

  for (const source of sources) {
    for (const [bodyId, targets] of Object.entries(source ?? {})) {
      if (!merged[bodyId]) {
        merged[bodyId] = targets.map((target) => ({
          objectId: target.objectId,
          primitiveIndices: target.primitiveIndices ? [...target.primitiveIndices] : undefined,
        }))
      }
    }
  }

  return Object.keys(merged).length > 0 ? merged : undefined
}

/**
 * 写入单个 hierarchy 节点对应的导入 metadata。
 *
 * @param object 需要写入 metadata 的场景对象。
 * @param fileId 当前对象所属文件。
 * @param hierarchy 当前对象的 hierarchy 镜像。
 * @param assemblyNodeId 项目模型节点 key。
 */
function writeHierarchyMetadata(
  object: THREE.Object3D,
  fileId: string,
  hierarchy: GlbHierarchyNode,
  assemblyNodeId: string,
): void {
  ImportedModelObjectMetadataService.writeMetadata(object, {
    fileId,
    hierarchy: {
      fileId,
      name: hierarchy.name,
      path: hierarchy.path,
      nodeType: hierarchy.nodeType,
      selectable: hierarchy.selectable,
      parentObjectId: hierarchy.parentObjectId,
      depth: hierarchy.depth,
      meta: cloneHierarchyMetaWithRuntimeAssemblyNodeId(hierarchy.meta, assemblyNodeId),
    },
    selectable: hierarchy.selectable,
    assemblyNodeId,
    selectionTarget: {
      assemblyNodeId,
    },
  })
}

/**
 * 判断节点包内部 hierarchy 节点是否代表可独立移动的装配/零件组件。
 *
 * 设计说明：`.puppy` 包内部的 assembly/part 节点拥有自己的稳定节点 ID，移动零部件必须按这些 ID
 * 定位组件；Body 只是零件内部实体，即使历史 meta 残留 `semanticKind=part`，也不能成为独立移动单元。
 *
 * @param node 节点包内部 hierarchy 节点。
 * @returns 是否应使用该节点自己的 assemblyNodeId。
 */
/**
 * 克隆 hierarchy meta，并把运行时装配身份同步到项目 occurrence。
 *
 * @param meta 原始 hierarchy meta。
 * @param assemblyNodeId 当前项目运行时装配节点 ID。
 * @returns 写入 Object3D metadata 的 hierarchy meta。
 */
function cloneHierarchyMetaWithRuntimeAssemblyNodeId(
  meta: GlbHierarchyNodeMeta | undefined,
  assemblyNodeId: string,
): GlbHierarchyNodeMeta | undefined {
  if (!meta) {
    return undefined
  }

  const clonedMeta = cloneGlbHierarchyUserDataMeta(meta)
  const bodyEntries = cloneRuntimeBodyEntries(clonedMeta.bodyEntries, assemblyNodeId)
  return {
    ...clonedMeta,
    assemblyNodeId,
    bodyEntries,
    bodyCount: bodyEntries ? bodyEntries.length : clonedMeta.bodyCount,
    bodyPrimitiveIndicesById: cloneRuntimeBodyPrimitiveIndicesById(clonedMeta.bodyPrimitiveIndicesById),
    bodyHighlightTargetsById: cloneRuntimeBodyHighlightTargetsById(clonedMeta.bodyHighlightTargetsById),
  }
}

/**
 * 将 bodyBindingId 重绑定到当前项目 occurrence。
 *
 * @param bodyBindingId 原始 bodyBindingId。
 * @param assemblyNodeId 当前项目组件 occurrence。
 * @returns 重绑定后的 bodyBindingId。
 */
function rebaseRuntimeBodyBindingId(bodyBindingId: unknown, assemblyNodeId: string): string {
  return rebaseImportedBodyOccurrenceBindingId(
    typeof bodyBindingId === 'string' ? bodyBindingId : '',
    assemblyNodeId,
  )
}

/**
 * 将 bodyId/key 归一为稳定 body 定义 ID。
 *
 * 设计说明：
 * `bodyId` 是零件内稳定定义，不承载项目 occurrence；只有 `bodyBindingId` 才表示当前组件实例里的交互绑定。
 * 组合项目模型时如果继续把 scoped 技术 ID 写入 bodyId，刷新后的设计树和选择元数据都会把技术身份当显示实体传播。
 *
 * @param bodyReferenceId bodyId 或高亮表 key。
 * @returns 稳定 body 定义 ID；普通语义 bodyId 保持不变。
 */
function normalizeRuntimeBodyReferenceId(bodyReferenceId: unknown): string {
  const normalizedBodyReferenceId = typeof bodyReferenceId === 'string' ? bodyReferenceId.trim() : ''
  if (!normalizedBodyReferenceId) {
    return normalizedBodyReferenceId
  }

  return resolveImportedBodyDefinitionId(normalizedBodyReferenceId) || normalizedBodyReferenceId
}

/**
 * 克隆 body entry，并把交互归属重绑定到当前项目 occurrence。
 *
 * @param entry 原始 body entry。
 * @param assemblyNodeId 当前项目组件 occurrence。
 * @returns 重绑定后的 body entry。
 */
function cloneRuntimeBodyEntry(entry: ImportedBodyEntry, assemblyNodeId: string): ImportedBodyEntry {
  const bodyBindingId = rebaseRuntimeBodyBindingId(entry.bodyBindingId, assemblyNodeId)
  return {
    ...entry,
    bodyId: normalizeRuntimeBodyReferenceId(entry.bodyId) || entry.bodyId,
    ...(bodyBindingId ? { bodyBindingId } : {}),
  }
}

/**
 * 克隆 body entry 集合，并统一重绑定 bodyBindingId。
 *
 * @param entries 原始 body entry 集合。
 * @param assemblyNodeId 当前项目组件 occurrence。
 * @returns 重绑定后的集合；没有条目时返回 undefined。
 */
function cloneRuntimeBodyEntries(
  entries: readonly ImportedBodyEntry[] | undefined,
  assemblyNodeId: string,
): ImportedBodyEntry[] | undefined {
  if (!Array.isArray(entries) || entries.length === 0) {
    return undefined
  }
  return entries.map((entry) => cloneRuntimeBodyEntry(entry, assemblyNodeId))
}

/**
 * 克隆 bodyPrimitiveIndicesById，并修正 scoped 技术 key。
 *
 * @param source 原始 primitive 索引表。
 * @returns 克隆后的 primitive 索引表。
 */
function cloneRuntimeBodyPrimitiveIndicesById(
  source: GlbHierarchyNodeMeta['bodyPrimitiveIndicesById'],
): GlbHierarchyNodeMeta['bodyPrimitiveIndicesById'] {
  if (!source) {
    return undefined
  }

  const cloned: NonNullable<GlbHierarchyNodeMeta['bodyPrimitiveIndicesById']> = {}
  Object.entries(source).forEach(([bodyId, primitiveIndices]) => {
    cloned[normalizeRuntimeBodyReferenceId(bodyId) || bodyId] = [...primitiveIndices]
  })
  return Object.keys(cloned).length > 0 ? cloned : undefined
}

/**
 * 克隆 bodyHighlightTargetsById，并修正 scoped 技术 key。
 *
 * @param source 原始高亮目标表。
 * @returns 克隆后的高亮目标表。
 */
function cloneRuntimeBodyHighlightTargetsById(
  source: GlbHierarchyNodeMeta['bodyHighlightTargetsById'],
): GlbHierarchyNodeMeta['bodyHighlightTargetsById'] {
  if (!source) {
    return undefined
  }

  const cloned: NonNullable<GlbHierarchyNodeMeta['bodyHighlightTargetsById']> = {}
  Object.entries(source).forEach(([bodyId, targets]) => {
    cloned[normalizeRuntimeBodyReferenceId(bodyId) || bodyId] = targets.map((target) => ({ ...target }))
  })
  return Object.keys(cloned).length > 0 ? cloned : undefined
}

/**
 * 克隆运行时选择目标，并把 assemblyNodeId 改写到当前项目 occurrence。
 *
 * @param target 原始选择目标。
 * @param assemblyNodeId 当前项目运行时装配节点 ID。
 * @returns 改写后的选择目标。
 */
function cloneRuntimeSelectionTarget(
  target: ImportedModelSelectionTargetMetadata | undefined,
  assemblyNodeId: string,
): ImportedModelSelectionTargetMetadata | undefined {
  if (!target) {
    return undefined
  }

  return {
    ...target,
    assemblyNodeId,
  }
}

/**
 * 克隆 body 级选择目标，并把每个 body 的 owner 改写到当前项目 occurrence。
 *
 * @param targets 原始 body 级选择目标。
 * @param assemblyNodeId 当前项目运行时装配节点 ID。
 * @returns 改写后的 body 级选择目标。
 */
function cloneRuntimeBodyBindingSelectionTargetsById(
  targets: ImportedModelObjectMetadata['bodyBindingSelectionTargetsById'],
  assemblyNodeId: string,
): ImportedModelObjectMetadata['bodyBindingSelectionTargetsById'] {
  if (!targets) {
    return undefined
  }

  const clonedTargets: Record<string, ImportedModelSelectionTargetMetadata> = {}
  Object.entries(targets).forEach(([bodyBindingId, target]) => {
    const rebasedBodyBindingId = rebaseRuntimeBodyBindingId(bodyBindingId, assemblyNodeId)
    if (!rebasedBodyBindingId) {
      return
    }

    clonedTargets[rebasedBodyBindingId] = {
      ...target,
      assemblyNodeId,
    }
  })

  return Object.keys(clonedTargets).length > 0 ? clonedTargets : undefined
}

/**
 * 克隆运行时子对象的 hierarchy metadata，并同步项目 occurrence。
 *
 * @param metadata 运行时子对象 metadata。
 * @param fileId 当前包文件 ID。
 * @param assemblyNodeId 当前项目运行时装配节点 ID。
 * @returns 可写回 Object3D 的 hierarchy metadata。
 */
/**
 * 判断未知值是否是普通对象记录。
 *
 * @param value 待判断的值。
 * @returns 是普通对象时返回 true。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/**
 * 克隆 primitive entity entry，并把 bodyBindingId 重绑定到当前项目 occurrence。
 *
 * @param entry 原始 primitive entity entry。
 * @param assemblyNodeId 当前项目组件 occurrence。
 * @returns 重绑定后的 entry；非法输入返回 null。
 */
function cloneRuntimePrimitiveEntityEntry(
  entry: unknown,
  assemblyNodeId: string,
): Record<string, unknown> | null {
  if (!isRecord(entry)) {
    return null
  }

  const bodyBindingId = rebaseRuntimeBodyBindingId(entry.bodyBindingId, assemblyNodeId)
  const bodyId = normalizeRuntimeBodyReferenceId(entry.bodyId)
  return {
    ...entry,
    ...(bodyId ? { bodyId } : {}),
    ...(bodyBindingId ? { bodyBindingId } : {}),
  }
}

/**
 * 克隆 primitive entity entry 集合。
 *
 * @param entries 原始 userData.primitiveEntityEntries。
 * @param assemblyNodeId 当前项目组件 occurrence。
 * @returns 重绑定后的集合；没有有效条目时返回 undefined。
 */
function cloneRuntimePrimitiveEntityEntries(
  entries: unknown,
  assemblyNodeId: string,
): Record<string, unknown>[] | undefined {
  if (!Array.isArray(entries) || entries.length === 0) {
    return undefined
  }

  const clonedEntries = entries.flatMap((entry) => {
    const clonedEntry = cloneRuntimePrimitiveEntityEntry(entry, assemblyNodeId)
    return clonedEntry ? [clonedEntry] : []
  })
  return clonedEntries.length > 0 ? clonedEntries : undefined
}

/**
 * 同步 Object3D userData 中的导入 body 绑定身份。
 *
 * 设计说明：
 * `.puppy` 可见 mesh 的 bodyEntries/primitiveEntityEntries 是 topology 与设计树共同消费的运行时入口。
 * 组合层把包内节点挂到项目 occurrence 后，必须把这些 userData 里的 bodyBindingId 一并提升，
 * 否则刷新后命中面时会从旧包内 occurrence 反推出错误 transform root。
 *
 * @param object 运行时 Object3D。
 * @param assemblyNodeId 当前项目组件 occurrence。
 */
function syncRuntimeObjectUserDataBodyBindings(object: THREE.Object3D, assemblyNodeId: string): void {
  const bodyEntries = cloneRuntimeBodyEntries(
    parseImportedBodyEntries(object.userData?.importedPartBodyEntries),
    assemblyNodeId,
  )
  const primitiveEntityEntries = cloneRuntimePrimitiveEntityEntries(
    object.userData?.primitiveEntityEntries,
    assemblyNodeId,
  )
  if (!bodyEntries && !primitiveEntityEntries) {
    return
  }

  object.userData = {
    ...object.userData,
    ...(bodyEntries ? { importedPartBodyEntries: bodyEntries } : {}),
    ...(primitiveEntityEntries ? { primitiveEntityEntries } : {}),
  }
}

/**
 * 克隆运行时子对象的 hierarchy metadata，并同步项目 occurrence。
 *
 * @param metadata 运行时子对象 metadata。
 * @param fileId 当前包文件 ID。
 * @param assemblyNodeId 当前项目运行时装配节点 ID。
 * @returns 可写回 Object3D 的 hierarchy metadata。
 */
function cloneRuntimeDescendantHierarchyMetadata(
  metadata: ImportedModelObjectMetadata,
  fileId: string,
  assemblyNodeId: string,
  demoteComponentSemantic = false,
): ImportedModelObjectMetadata['hierarchy'] {
  if (!metadata.hierarchy) {
    return undefined
  }

  const meta = cloneHierarchyMetaWithRuntimeAssemblyNodeId(
    metadata.hierarchy.meta,
    assemblyNodeId,
  )
  if (
    demoteComponentSemantic
    && meta
    && (meta.semanticKind === 'assembly' || meta.semanticKind === 'part')
  ) {
    meta.semanticKind = 'unknown'
  }

  return {
    ...metadata.hierarchy,
    fileId,
    meta,
  }
}

/**
 * 同步运行时 imported 子树中未被独立组件层级接管的 metadata。
 *
 * 设计说明：`.puppy` 的 display mesh / pick host 可能只是运行时显示或拓扑对象，
 * 不出现在 assembly hierarchy 中。它们仍会被射线拾取和移动命令命中，因此必须继承最近组件
 * 的项目 occurrence 身份，不能继续携带包内部 assemblyNodeId。零件包还会使用虚拟 Body 节点，
 * 此时真实 Mesh 不会出现在 hierarchy objectId 集合中，组合层必须从包根整体同步运行时子树。
 *
 * @param input 同步输入。
 */
function syncRuntimeImportedSubtreeMetadata(input: {
  readonly root: THREE.Object3D
  readonly fileId: string
  readonly assemblyNodeId: string
  readonly childHierarchyObjectIds: ReadonlySet<string>
  readonly includeRoot?: boolean
  readonly componentRootObjectId?: string
}): void {
  const visit = (object: THREE.Object3D): void => {
    if (input.childHierarchyObjectIds.has(object.uuid)) {
      return
    }

    syncRuntimeObjectUserDataBodyBindings(object, input.assemblyNodeId)

    const metadata = ImportedModelObjectMetadataService.peekOwnMetadata(object)
    if (metadata) {
      ImportedModelObjectMetadataService.writeMetadata(object, {
        fileId: input.fileId,
        hierarchy: cloneRuntimeDescendantHierarchyMetadata(
          metadata,
          input.fileId,
          input.assemblyNodeId,
          Boolean(input.componentRootObjectId),
        ),
        selectable: metadata.selectable,
        assemblyNodeId: input.assemblyNodeId,
        partFileUuid: metadata.partFileUuid,
        selectionProxyObjectId: input.componentRootObjectId ?? metadata.selectionProxyObjectId,
        selectionTarget: cloneRuntimeSelectionTarget(metadata.selectionTarget, input.assemblyNodeId),
        bodyBindingSelectionTargetsById: cloneRuntimeBodyBindingSelectionTargetsById(
          metadata.bodyBindingSelectionTargetsById,
          input.assemblyNodeId,
        ),
        fileInfo: metadata.fileInfo,
        runtimeWrapper: metadata.runtimeWrapper,
        ...(input.componentRootObjectId ? { componentTransformRoot: false } : {}),
      })
    }

    object.children.forEach(visit)
  }

  if (input.includeRoot) {
    visit(input.root)
    return
  }

  input.root.children.forEach(visit)
}

function isPackageComponentHierarchyNode(node: GlbHierarchyNode): boolean {
  const semanticKind = node.meta?.semanticKind
  return node.nodeType !== 'Body' && (semanticKind === 'assembly' || semanticKind === 'part')
}

/**
 * 解析节点包内部对象写入运行时 metadata 时应使用的组件 ID。
 *
 * @param node 当前 hierarchy 节点。
 * @param inheritedAssemblyNodeId 最近父级组件 ID。
 * @returns 当前对象应归属的运行时组件 ID。
 */
function resolvePackageHierarchyRuntimeAssemblyNodeId(
  node: GlbHierarchyNode,
  inheritedAssemblyNodeId: string,
  packageMountAssemblyNodeId: string,
): string {
  const nodeAssemblyNodeId = node.meta?.assemblyNodeId?.trim()
  return isPackageComponentHierarchyNode(node) && nodeAssemblyNodeId
    ? resolveScopedPackageRuntimeAssemblyNodeId(nodeAssemblyNodeId, packageMountAssemblyNodeId)
    : inheritedAssemblyNodeId
}

/**
 * 将节点包内部 hierarchy 挂到 OpenProject 节点容器之下。
 *
 * @param input 重映射输入。
 * @returns 重映射后的 hierarchy 节点。
 */
function rebasePackageHierarchy(input: {
  readonly source: GlbHierarchyNode
  readonly parentObjectId: string
  readonly parentPath: string
  readonly parentDepth: number
  readonly fileId: string
  readonly assemblyNodeId: string
  readonly packageMountAssemblyNodeId: string
  readonly objectByUuid: ReadonlyMap<string, THREE.Object3D>
}): GlbHierarchyNode {
  const path = input.parentPath ? `${input.parentPath}/${input.source.name}` : input.source.name
  const runtimeAssemblyNodeId = resolvePackageHierarchyRuntimeAssemblyNodeId(
    input.source,
    input.assemblyNodeId,
    input.packageMountAssemblyNodeId,
  )
  const node: GlbHierarchyNode = {
    objectId: input.source.objectId,
    parentObjectId: input.parentObjectId,
    path,
    name: input.source.name,
    nodeType: input.source.nodeType,
    selectable: input.source.selectable,
    depth: input.parentDepth + 1,
    meta: cloneHierarchyMetaWithRuntimeAssemblyNodeId(input.source.meta, runtimeAssemblyNodeId),
    children: [],
  }
  node.children = input.source.children.map((child) =>
    rebasePackageHierarchy({
      source: child,
      parentObjectId: node.objectId,
      parentPath: node.path,
      parentDepth: node.depth,
      fileId: input.fileId,
      assemblyNodeId: runtimeAssemblyNodeId,
      packageMountAssemblyNodeId: input.packageMountAssemblyNodeId,
      objectByUuid: input.objectByUuid,
    }))

  const object = input.objectByUuid.get(node.objectId)
  if (object) {
    syncRuntimeObjectUserDataBodyBindings(object, runtimeAssemblyNodeId)
    writeHierarchyMetadata(object, input.fileId, node, runtimeAssemblyNodeId)
    syncRuntimeImportedSubtreeMetadata({
      root: object,
      fileId: input.fileId,
      assemblyNodeId: runtimeAssemblyNodeId,
      childHierarchyObjectIds: new Set(input.source.children.map((child) => child.objectId)),
    })
  }

  return node
}

/**
 * 读取层级路径末段，作为折叠后的节点名称兜底。
 *
 * @param path 层级路径。
 * @returns 路径末段；路径为空时返回空字符串。
 */
function getHierarchyPathLastSegment(path: string): string {
  return path.split('/').filter(Boolean).at(-1) ?? ''
}

/**
 * 解析包根直接几何折叠后的实体显示名。
 *
 * @param source 包根 hierarchy。
 * @param foldedRootName OpenProject 节点显示名。
 * @returns 避免重复节点名的实体显示名。
 */
function resolveFoldedRootGeometryNodeName(
  source: GlbHierarchyNode,
  foldedRootName: string,
): string {
  const bodyEntries = source.meta?.bodyEntries
  if (Array.isArray(bodyEntries) && bodyEntries.length === 1) {
    const bodyEntry = bodyEntries[0]
    const bodyLabel = bodyEntry?.label?.trim()
    if (bodyEntry && bodyLabel && !isRuntimeImportedBodyEntryLabel(bodyEntry)) {
      return bodyLabel
    }
  }

  const sourceName = source.name.trim()
  if (
    sourceName
    && sourceName !== foldedRootName
    && !sourceName.startsWith('mesh:')
    && !sourceName.includes('::')
  ) {
    return sourceName
  }

  return '实体1'
}

/**
 * 判断包根直接几何是否已经被子 body 节点表达。
 *
 * @param source 包根 hierarchy。
 * @returns 存在 body 子节点时返回 true。
 */
function hasPromotedRootBodyNode(source: GlbHierarchyNode): boolean {
  return source.children.some((child) => (
    child.nodeType === 'Body'
    || child.meta?.selectionObjectId === source.objectId
  ))
}

/**
 * 创建包根直接几何被折叠后的实体节点。
 *
 * @param input 折叠输入。
 * @param foldedRootName OpenProject 节点显示名。
 * @returns 可显示在 OpenProject 节点下的实体层级。
 */
function createFoldedRootGeometryNode(
  input: {
    readonly source: GlbHierarchyNode
    readonly parentObjectId: string
    readonly parentPath: string
    readonly parentDepth: number
    readonly assemblyNodeId: string
  },
  foldedRootName: string,
): GlbHierarchyNode {
  const nodeName = resolveFoldedRootGeometryNodeName(input.source, foldedRootName)
  const sourceMeta = input.source.meta
    ? cloneHierarchyMetaWithRuntimeAssemblyNodeId(input.source.meta, input.assemblyNodeId)
    : undefined
  const bodyEntries = Array.isArray(sourceMeta?.bodyEntries) && sourceMeta.bodyEntries.length > 0
    ? sourceMeta.bodyEntries
    : [{ bodyId: input.source.objectId, label: nodeName }]
  return {
    objectId: input.source.objectId,
    parentObjectId: input.parentObjectId,
    path: input.parentPath ? `${input.parentPath}/${nodeName}` : nodeName,
    name: nodeName,
    nodeType: 'Body',
    selectable: input.source.selectable,
    depth: input.parentDepth + 1,
    meta: sourceMeta
      ? {
          ...sourceMeta,
          bodyCount: bodyEntries.length,
          bodyEntries,
          selectionObjectId: sourceMeta.selectionObjectId ?? input.source.objectId,
        }
      : undefined,
    children: [],
  }
}

/**
 * 把节点包内部的根 hierarchy 折叠到 OpenProject 节点上。
 *
 * @param input 需要折叠的包层级输入。
 * @returns 折叠根节点后可直接挂到 OpenProject 节点下的可见子层级。
 */
function rebaseFoldedPackageHierarchy(input: {
  readonly source: GlbHierarchyNode
  readonly ownerNodeType: ModelAssetNode['nodeType']
  readonly parentObjectId: string
  readonly parentPath: string
  readonly parentDepth: number
  readonly fileId: string
  readonly assemblyNodeId: string
  readonly packageMountAssemblyNodeId: string
  readonly runtimeRoot: THREE.Object3D
  readonly objectByUuid: ReadonlyMap<string, THREE.Object3D>
}): readonly GlbHierarchyNode[] {
  const semanticRoot = input.objectByUuid.get(input.source.objectId)
  if (semanticRoot && semanticRoot !== input.runtimeRoot) {
    // 包声明的 hierarchy 根才是业务起点；其外部物理祖先只承载 GLTF scene/文档矩阵。
    // 必须在加载边界显式标记，否则二次导出会给中间容器分配新节点，隔断同 occurrence 的折叠。
    const wrappers: THREE.Object3D[] = []
    let ancestor = semanticRoot.parent
    while (ancestor && ancestor !== input.runtimeRoot) {
      wrappers.push(ancestor)
      ancestor = ancestor.parent
    }
    if (!ancestor) {
      throw new Error(`项目节点包语义根不在挂载根内：assemblyNodeId=${input.assemblyNodeId}, objectId=${input.source.objectId}`)
    }
    wrappers.push(input.runtimeRoot)
    // 先校验整条路径再写标记，不能把有独立渲染负载的祖先误当作空容器。
    if (wrappers.some(object => object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points || object instanceof THREE.Sprite)) {
      throw new Error(`项目节点包包装路径包含几何对象：assemblyNodeId=${input.assemblyNodeId}`)
    }
    wrappers.forEach(object => ImportedModelObjectMetadataService.writeMetadata(object, { runtimeWrapper: true }))
  }
  if (input.ownerNodeType === 'part') {
    /**
     * 零件包的结构/Feature 容器会在设计树中折叠，Body 节点则可能是没有对应 Object3D 的虚拟节点。
     * 因此不能依赖 hierarchy objectId 逐节点改写，而要在项目零件挂载边界统一认领完整运行时子树。
     * 该规则只适用于声明为 part 的项目节点；装配包仍按其内部组件边界分别保留 occurrence。
     */
    const bodyHierarchy = rebaseFoldedPartPackageBodyHierarchy(input)
    syncRuntimeImportedSubtreeMetadata({
      root: input.runtimeRoot,
      fileId: input.fileId,
      assemblyNodeId: input.assemblyNodeId,
      childHierarchyObjectIds: new Set(),
      includeRoot: true,
      componentRootObjectId: input.parentObjectId,
    })
    return bodyHierarchy
  }

  const foldedRootName = getHierarchyPathLastSegment(input.parentPath) || input.source.name
  const foldedRoot: GlbHierarchyNode = {
    objectId: input.source.objectId,
    parentObjectId: input.parentObjectId,
    path: input.parentPath || input.source.name,
    name: foldedRootName,
    nodeType: input.source.nodeType,
    selectable: input.source.selectable,
    depth: input.parentDepth,
    meta: input.source.meta ? cloneGlbHierarchyUserDataMeta(input.source.meta) : undefined,
    children: [],
  }
  const foldedRootObject = input.objectByUuid.get(foldedRoot.objectId)
  if (foldedRootObject) {
    // 包根对象仍然保留项目节点 metadata，保证射线拾取和程序化高亮可以回到同一个 assemblyNodeId。
    writeHierarchyMetadata(
      foldedRootObject,
      input.fileId,
      foldedRoot,
      input.assemblyNodeId,
    )
  }

  const children = input.source.children.map((child) =>
    rebasePackageHierarchy({
      source: child,
      parentObjectId: input.parentObjectId,
      parentPath: input.parentPath,
      parentDepth: input.parentDepth,
      fileId: input.fileId,
      assemblyNodeId: input.assemblyNodeId,
      packageMountAssemblyNodeId: input.packageMountAssemblyNodeId,
      objectByUuid: input.objectByUuid,
    }))

  if (input.source.meta?.hasDirectGeometry && !hasPromotedRootBodyNode(input.source)) {
    const foldedRootGeometryNode = createFoldedRootGeometryNode(input, foldedRootName)
    if (foldedRootObject) {
      // 包根自身就是几何实体时，metadata 应对齐可见的实体节点，而不是被折叠掉的包装层。
      writeHierarchyMetadata(
        foldedRootObject,
        input.fileId,
        foldedRootGeometryNode,
        input.assemblyNodeId,
      )
    }
    return [foldedRootGeometryNode, ...children]
  }

  return children
}

/**
 * 把完整零件包内部可能残留的结构层级折叠成实体层级。
 *
 * 设计原因：OpenProject 子节点已经明确声明当前节点是零件还是装配体；当父装配体下的零件节点切换到完整
 * `.puppy` 包时，包内 hierarchy 可能仍携带导出时的 `part` 或 `feature` 容器。零件节点的设计树不应再展示
 * 这些包内结构容器，只需要把可选实体语义提升到项目零件节点下。
 *
 * @param input 包层级折叠输入。
 * @returns 可直接挂到项目零件节点下的实体层级。
 */
function rebaseFoldedPartPackageBodyHierarchy(input: {
  readonly source: GlbHierarchyNode
  readonly parentObjectId: string
  readonly parentPath: string
  readonly parentDepth: number
  readonly fileId: string
  readonly assemblyNodeId: string
  readonly objectByUuid: ReadonlyMap<string, THREE.Object3D>
}): readonly GlbHierarchyNode[] {
  if (input.source.nodeType === 'Body') {
    return [rebasePackageHierarchy({
      ...input,
      packageMountAssemblyNodeId: input.assemblyNodeId,
    })]
  }

  const bodyNodes: GlbHierarchyNode[] = []
  if (input.source.meta?.hasDirectGeometry && !hasPromotedRootBodyNode(input.source)) {
    const foldedRootName = getHierarchyPathLastSegment(input.parentPath) || input.source.name
    const foldedRootGeometryNode = createFoldedRootGeometryNode(input, foldedRootName)
    const foldedRootObject = input.objectByUuid.get(foldedRootGeometryNode.objectId)
    if (foldedRootObject) {
      // 被折叠掉的包内结构不能继续拥有独立零件身份，运行时选择必须回到当前项目零件节点。
      writeHierarchyMetadata(
        foldedRootObject,
        input.fileId,
        foldedRootGeometryNode,
        input.assemblyNodeId,
      )
    }
    bodyNodes.push(foldedRootGeometryNode)
  }

  for (const child of input.source.children) {
    bodyNodes.push(...rebaseFoldedPartPackageBodyHierarchy({
      ...input,
      source: child,
    }))
  }

  return bodyNodes
}

/**
 * 创建项目模型节点的 hierarchy 元数据。
 *
 * @param node 项目模型节点。
 * @param hasGeometry 子树是否存在几何。
 * @param hasDirectGeometry 当前节点是否直接挂载了节点包几何。
 * @returns GlbHierarchyNode 使用的语义元数据。
 */
/**
 * 从折叠后的直接 Body 子节点收集实体语义。
 *
 * 设计原因：FeatureManager 不直接展示 `nodeType=Body` 子节点，而是从父节点
 * `meta.bodyEntries` 生成“实体(N)”文件夹；因此包根被折叠后，直接 Body 子节点的语义
 * 必须提升回 OpenProject 节点。
 *
 * @param bodyNodes 折叠后直接挂在项目节点下的 Body 节点。
 * @returns 可写入项目节点 meta 的实体语义。
 */
function collectDirectBodyNodeSemanticMeta(
  bodyNodes: readonly GlbHierarchyNode[],
): ProjectNodePackageSemanticMeta | undefined {
  const bodyEntries = mergeBodyEntriesById(...bodyNodes.map((node) => node.meta?.bodyEntries))
  if (bodyEntries.length === 0) {
    return undefined
  }

  const bodyHighlightTargetsById = mergeBodyHighlightTargetsById(
    ...bodyNodes.map((node) => {
      const entries = node.meta?.bodyEntries ?? []
      if (!node.meta?.highlightTargets || entries.length === 0) {
        return undefined
      }

      return Object.fromEntries(
        entries.map((entry) => [
          entry.bodyId,
          node.meta!.highlightTargets!,
        ]),
      )
    }),
  )

  return {
    bodyEntries,
    bodyPrimitiveIndicesById: mergeBodyPrimitiveIndicesById(
      ...bodyNodes.map((node) => node.meta?.bodyPrimitiveIndicesById),
    ),
    bodyHighlightTargetsById,
    materialNames: Array.from(new Set(bodyNodes.flatMap((node) => node.meta?.materialNames ?? []))),
  }
}

/**
 * 收集被折叠包根应提升到项目节点的实体与材质语义。
 *
 * @param source 节点包原始 hierarchy 根。
 * @param foldedChildren 折叠后挂到 OpenProject 节点下的可见子层级。
 * @returns OpenProject 节点需要继承的包内语义。
 */
function collectFoldedPackageSemanticMeta(
  source: GlbHierarchyNode,
  foldedChildren: readonly GlbHierarchyNode[],
  assemblyNodeId: string,
): ProjectNodePackageSemanticMeta | undefined {
  const sourceMeta = source.meta
    ? cloneHierarchyMetaWithRuntimeAssemblyNodeId(source.meta, assemblyNodeId)
    : undefined
  const bodyNodeMeta = collectDirectBodyNodeSemanticMeta(
    foldedChildren.filter((child) => child.nodeType === 'Body'),
  )
  const bodyEntries = mergeBodyEntriesById(sourceMeta?.bodyEntries, bodyNodeMeta?.bodyEntries)
  const materialNames = Array.from(new Set([
    ...(sourceMeta?.materialNames ?? []),
    ...(bodyNodeMeta?.materialNames ?? []),
  ]))
  const bodyPrimitiveIndicesById = mergeBodyPrimitiveIndicesById(
    sourceMeta?.bodyPrimitiveIndicesById,
    bodyNodeMeta?.bodyPrimitiveIndicesById,
  )
  const bodyHighlightTargetsById = mergeBodyHighlightTargetsById(
    sourceMeta?.bodyHighlightTargetsById,
    bodyNodeMeta?.bodyHighlightTargetsById,
  )
  const materialName = sourceMeta?.materialName ?? (materialNames.length === 1 ? materialNames[0] : undefined)

  if (
    bodyEntries.length === 0
    && materialNames.length === 0
    && !materialName
    && !bodyPrimitiveIndicesById
    && !bodyHighlightTargetsById
  ) {
    return undefined
  }

  return {
    bodyEntries,
    bodyPrimitiveIndicesById,
    bodyHighlightTargetsById,
    materialNames,
    materialName,
  }
}

/**
 * 合并同一个 OpenProject 节点上多个节点包的实体语义。
 *
 * @param metas 每个节点包提升出的语义片段。
 * @returns 写入项目节点 meta 的合并结果。
 */
function mergeProjectNodePackageSemanticMetas(
  metas: readonly ProjectNodePackageSemanticMeta[],
): ProjectNodePackageSemanticMeta | undefined {
  const bodyEntries = mergeBodyEntriesById(...metas.map((meta) => meta.bodyEntries))
  const materialNames = Array.from(new Set(metas.flatMap((meta) => meta.materialNames)))
  const bodyPrimitiveIndicesById = mergeBodyPrimitiveIndicesById(
    ...metas.map((meta) => meta.bodyPrimitiveIndicesById),
  )
  const bodyHighlightTargetsById = mergeBodyHighlightTargetsById(
    ...metas.map((meta) => meta.bodyHighlightTargetsById),
  )
  const materialName = metas.find((meta) => meta.materialName)?.materialName
    ?? (materialNames.length === 1 ? materialNames[0] : undefined)

  if (
    bodyEntries.length === 0
    && materialNames.length === 0
    && !materialName
    && !bodyPrimitiveIndicesById
    && !bodyHighlightTargetsById
  ) {
    return undefined
  }

  return {
    bodyEntries,
    bodyPrimitiveIndicesById,
    bodyHighlightTargetsById,
    materialNames,
    materialName,
  }
}

/**
 * 创建项目模型节点的 hierarchy 元数据。
 *
 * 设计说明：OpenProject 节点包根被折叠后，实体文件夹需要从项目节点自身
 * `meta.bodyEntries` 生成，因此这里接收由节点包提升出的实体与材质语义。
 *
 * @param node 项目模型节点。
 * @param runtimeAssemblyNodeId 当前节点统一写入选择、变换和拓扑链路的装配身份。
 * @param hasGeometry 当前节点子树是否存在几何。
 * @param hasDirectGeometry 当前节点是否直接挂载节点包几何。
 * @param packageSemanticMeta 节点包提升出的实体与材质语义。
 * @returns GlbHierarchyNode 使用的语义元数据。
 */
function createProjectNodeMeta(
  node: ModelAssetNode,
  runtimeAssemblyNodeId: string,
  hasGeometry: boolean,
  hasDirectGeometry: boolean,
  packageSemanticMeta?: ProjectNodePackageSemanticMeta,
): GlbHierarchyNodeMeta {
  const shouldInheritBodySemantics = node.nodeType === 'part'
  const bodyEntries = shouldInheritBodySemantics ? packageSemanticMeta?.bodyEntries ?? [] : []
  return {
    semanticKind: node.nodeType,
    hasGeometry,
    hasDirectGeometry,
    bodyCount: bodyEntries.length > 0 ? bodyEntries.length : undefined,
    bodyEntries: bodyEntries.length > 0 ? bodyEntries : undefined,
    bodyPrimitiveIndicesById: shouldInheritBodySemantics
      ? cloneBodyPrimitiveIndicesById(packageSemanticMeta?.bodyPrimitiveIndicesById)
      : undefined,
    bodyHighlightTargetsById: shouldInheritBodySemantics
      ? cloneBodyHighlightTargetsById(packageSemanticMeta?.bodyHighlightTargetsById)
      : undefined,
    materialNames: [...(packageSemanticMeta?.materialNames ?? [])],
    materialName: packageSemanticMeta?.materialName,
    assemblyNodeId: runtimeAssemblyNodeId,
    externalFileUuid: node.packageFiles[0]?.uuid || node.packageFiles[0]?.fileId,
    externalAssemblyName: resolveNodeDisplayName(node),
  }
}

/**
 * 注册节点容器与场景对象绑定。
 *
 * @param sceneBindings 场景绑定索引。
 * @param nodeKey 项目模型节点 key。
 * @param assemblyNodeId 当前项目节点挂载后的运行时装配 occurrence ID。
 * @param object 需要绑定的场景对象。
 */
function registerSceneBinding(
  sceneBindings: SceneBindingIndex,
  nodeKey: string,
  assemblyNodeId: string,
  object: THREE.Object3D,
): void {
  const assemblyObjectIds = sceneBindings.byAssemblyNodeId[assemblyNodeId] ?? []
  sceneBindings.byAssemblyNodeId[assemblyNodeId] = [...assemblyObjectIds, object.uuid]

  // 项目树绑定必须始终保留结构 nodeKey，不能被运行时 occurrence 重锚结果覆盖。
  const projectObjectIds = sceneBindings.byProjectNodeKey?.[nodeKey] ?? []
  if (!sceneBindings.byProjectNodeKey) {
    sceneBindings.byProjectNodeKey = {}
  }
  sceneBindings.byProjectNodeKey[nodeKey] = [...projectObjectIds, object.uuid]
  sceneBindings.byObject3DUuid[object.uuid] = object
}

/**
 * 以节点 key 分组节点包结果。
 *
 * @param packageResults 已解析的节点包结果。
 * @returns nodeKey 到包结果列表的映射。
 */
function groupPackageResultsByNodeKey(
  packageResults: readonly ProjectModelPackageResult[],
): ReadonlyMap<string, readonly ProjectModelPackageResult[]> {
  const grouped = new Map<string, ProjectModelPackageResult[]>()

  for (const packageResult of packageResults) {
    const current = grouped.get(packageResult.nodeKey) ?? []
    current.push(packageResult)
    grouped.set(packageResult.nodeKey, current)
  }

  return grouped
}

/**
 * 按前序遍历项目模型节点。
 *
 * @param node 遍历起点。
 * @returns 从当前节点开始的节点列表。
 */
function collectProjectModelNodesPreorder(node: ModelAssetNode): readonly ModelAssetNode[] {
  return [
    node,
    ...node.children.flatMap((child) => collectProjectModelNodesPreorder(child)),
  ]
}

/**
 * 归一化调用方传入的节点包结果。
 *
 * 设计说明：新加载链路按 `packageResults` 表达“节点 + 包文件 + 解析结果”；早期实现计划按
 * `loadedResultByNodeKey` 表达“节点 + 解析结果”。这里同时支持两种输入，避免调用方因为
 * 组合器入参演进而重复写转换逻辑。
 *
 * @param input 组合输入。
 * @returns 可按节点 key 分组的包结果列表。
 */
function normalizePackageResults(
  input: ComposeProjectModelImportedResultInput,
): readonly ProjectModelPackageResult[] {
  if (input.packageResults) {
    return input.packageResults
  }

  const loadedResultByNodeKey = input.loadedResultByNodeKey ?? new Map<string, ImportedModelResult>()
  return collectProjectModelNodesPreorder(input.root).flatMap((node) => {
    if (node.packageFiles.length === 0) {
      return []
    }
    const loadedResult = loadedResultByNodeKey.get(node.nodeKey)
    if (!loadedResult) {
      throw new Error(`项目模型节点缺少已加载几何结果：nodeKey=${node.nodeKey}`)
    }

    return [{
      nodeKey: node.nodeKey,
      file: node.packageFiles[0]!,
      result: loadedResult,
    }]
  })
}

/**
 * 合并子导入结果携带的语义资产缓存。
 *
 * @param result 子导入结果。
 * @param partAssetsByUuid 聚合后的零件资产表。
 * @param assemblyAssetsByUuid 聚合后的装配资产表。
 */
function mergeImportedResultAssets(
  result: ImportedModelResult,
  partAssetsByUuid: Record<string, PartAsset>,
  assemblyAssetsByUuid: Record<string, AssemblyModelAsset>,
): void {
  if (result.kind === 'cached-part' && result.asset) {
    partAssetsByUuid[result.asset.fileUuid] = result.asset
    return
  }

  if (result.kind !== 'composed-assembly') {
    return
  }

  Object.assign(partAssetsByUuid, result.partAssetsByUuid)
  Object.assign(assemblyAssetsByUuid, result.assemblyAssetsByUuid)
}

/**
 * 构建 imported-model manifest 的节点表。
 *
 * @param root 项目模型根节点。
 * @param runtimeAssemblyNodeIds 项目节点到权威运行时装配身份的映射。
 * @returns manifest 节点表、子节点表与引用文件 uuid。
 */
function buildProjectModelManifestIndexes(
  root: ModelAssetNode,
  runtimeAssemblyNodeIds: ReadonlyMap<ModelAssetNode, string>,
): {
  readonly nodesById: Record<string, AssemblyNode>
  readonly childIdsByNodeId: Record<string, string[]>
  readonly referencedPartUuids: string[]
} {
  const nodesById: Record<string, AssemblyNode> = {}
  const childIdsByNodeId: Record<string, string[]> = {}
  const referencedPartUuids = new Set<string>()

  const visit = (node: ModelAssetNode, parentNodeId: string | null): void => {
    const assemblyNodeId = resolveProjectModelRuntimeAssemblyNodeId(node, runtimeAssemblyNodeIds)
    const packageFile = node.packageFiles[0]
    const partFileUuid = packageFile ? packageFile.uuid || packageFile.fileId : undefined
    if (partFileUuid) {
      referencedPartUuids.add(partFileUuid)
    }

    nodesById[assemblyNodeId] = {
      nodeId: assemblyNodeId,
      parentNodeId,
      name: resolveNodeDisplayName(node),
      semanticKind: node.nodeType,
      transform: toMatrixTuple(node.localTransform),
      partFileUuid,
      partFileId: packageFile?.fileId,
    }
    childIdsByNodeId[assemblyNodeId] = node.children.map((child) => (
      resolveProjectModelRuntimeAssemblyNodeId(child, runtimeAssemblyNodeIds)
    ))

    for (const child of node.children) {
      visit(child, assemblyNodeId)
    }
  }

  visit(root, null)

  return {
    nodesById,
    childIdsByNodeId,
    referencedPartUuids: Array.from(referencedPartUuids),
  }
}

/**
 * 构建项目模型根 manifest。
 *
 * @param root 项目模型根节点。
 * @param runtimeAssemblyNodeIds 项目节点到权威运行时装配身份的映射。
 * @returns ComposedAssemblyResult 需要的 manifest。
 */
function buildProjectModelManifest(
  root: ModelAssetNode,
  runtimeAssemblyNodeIds: ReadonlyMap<ModelAssetNode, string>,
): AssemblyManifest {
  const manifestIndexes = buildProjectModelManifestIndexes(root, runtimeAssemblyNodeIds)
  const rootFile = root.assemblyManifest ?? root.packageFiles[0]
  const rootAssemblyNodeId = resolveProjectModelRuntimeAssemblyNodeId(root, runtimeAssemblyNodeIds)

  return {
    assemblyFileId: rootFile?.fileId ?? root.nodeKey,
    assemblyFileUuid: rootFile?.uuid || root.nodeKey,
    version: root.versionKey,
    rootNodeId: rootAssemblyNodeId,
    nodesById: manifestIndexes.nodesById,
    childIdsByNodeId: manifestIndexes.childIdsByNodeId,
    referencedPartUuids: manifestIndexes.referencedPartUuids,
  }
}

/**
 * 把包内组件根的 occurrence 变换交还给项目节点容器。
 *
 * `ModelAssetNode.localTransform` 已经保存同一份装配位姿。真实组件子树从容器包移动到
 * 项目 occurrence group 后必须把自身矩阵归一为单位矩阵，否则刷新后会叠加两次位姿。
 *
 * @param object 被项目 occurrence 认领的包内组件根。
 */
function resetClaimedPackageOccurrenceTransform(object: THREE.Object3D): void {
  object.position.set(0, 0, 0)
  object.quaternion.identity()
  object.scale.set(1, 1, 1)
  object.matrix.identity()
  object.matrixWorldNeedsUpdate = true
}

/**
 * 校验组合阶段写入单个场景对象的装配身份是否保持一致。
 *
 * 设计说明：V6 项目树、Object3D metadata、hierarchy 与 selectionIndex 必须使用同一份
 * assemblyNodeId。任何一个字段仍携带结构 nodeKey 都会在真实拾取时被变换根解析器拒绝，
 * 因此在加载阶段直接失败比让场景进入“能显示但不能编辑”的半有效状态更安全。
 *
 * @param input 当前对象、层级和选择索引的校验上下文。
 */
function assertProjectModelRuntimeAssemblyIdentity(input: {
  /** 错误日志使用的项目结构 key。 */
  readonly nodeKey: string
  /** 待校验的场景对象。 */
  readonly object: THREE.Object3D
  /** 当前对象对应的可选 hierarchy。 */
  readonly hierarchy?: GlbHierarchyNode
  /** 当前组合结果选择索引。 */
  readonly selectionIndex: SelectionIndex
  /** 权威运行时装配身份。 */
  readonly expectedAssemblyNodeId: string
}): void {
  const expectedAssemblyNodeId = normalizeAssemblyNodeId(input.expectedAssemblyNodeId)
  const metadata = ImportedModelObjectMetadataService.peekMetadata(input.object)
  const identityFields = [
    ['metadata.assemblyNodeId', metadata?.assemblyNodeId],
    ['metadata.selectionTarget.assemblyNodeId', metadata?.selectionTarget?.assemblyNodeId],
    ['metadata.hierarchy.meta.assemblyNodeId', metadata?.hierarchy?.meta?.assemblyNodeId],
    ['hierarchy.meta.assemblyNodeId', input.hierarchy?.meta?.assemblyNodeId],
    [
      'selectionIndex.assemblyNodeId',
      input.selectionIndex.byObject3DUuid[input.object.uuid]?.assemblyNodeId,
    ],
  ] as const
  const mismatchedFields = identityFields.filter(([, value]) => (
    typeof value === 'string'
    && value.trim().length > 0
    && normalizeAssemblyNodeId(value) !== expectedAssemblyNodeId
  ))

  if (mismatchedFields.length > 0) {
    throw new Error(
      `PuppyCAD 项目节点运行时装配身份不一致：nodeKey=${input.nodeKey}, objectUuid=${input.object.uuid}, expected=${expectedAssemblyNodeId}, actual=${mismatchedFields.map(([field, value]) => `${field}:${value}`).join(',')}`,
    )
  }
  if (!metadata?.assemblyNodeId || normalizeAssemblyNodeId(metadata.assemblyNodeId) !== expectedAssemblyNodeId) {
    throw new Error(
      `PuppyCAD 项目节点缺少权威运行时装配身份：nodeKey=${input.nodeKey}, objectUuid=${input.object.uuid}, expected=${expectedAssemblyNodeId}`,
    )
  }
}

/**
 * 当前项目节点要挂载的一份包语义子树。
 */
interface ProjectModelPackageMount {
  /** 容器包结果。 */
  readonly packageResult: ProjectModelPackageResult
  /** 当前节点真正拥有的场景对象根。 */
  readonly packageObject: THREE.Object3D
  /** 与场景对象根对应的 hierarchy。 */
  readonly packageHierarchy: GlbHierarchyNode
  /** 是否需要在此处首次合并包级资产与警告。 */
  readonly shouldMergePackageAssets: boolean
  /** 是否是从父容器中认领的 occurrence 子树。 */
  readonly isEmbeddedOccurrenceClaim: boolean
  /** 当前包原始 hierarchy 身份所属的项目挂载节点。 */
  readonly runtimeScopeNode: ModelAssetNode
}

/**
 * 创建当前项目节点的全部包挂载源。
 *
 * @param input 独立节点包结果与可选内嵌 occurrence 认领。
 * @returns 独立包和内嵌真实子树的统一挂载描述。
 */
function createProjectModelPackageMounts(input: {
  readonly ownerNode: ModelAssetNode
  readonly packageResults: readonly ProjectModelPackageResult[]
  readonly embeddedClaim?: EmbeddedPuppyOccurrenceClaim
}): readonly ProjectModelPackageMount[] {
  const directMounts: ProjectModelPackageMount[] = input.packageResults.map((packageResult) => ({
    packageResult,
    packageObject: resolveImportedResultRootObject(packageResult.result),
    packageHierarchy: resolveImportedResultHierarchy(packageResult.result),
    shouldMergePackageAssets: true,
    isEmbeddedOccurrenceClaim: false,
    runtimeScopeNode: input.ownerNode,
  }))
  if (!input.embeddedClaim) {
    return directMounts
  }
  return [
    ...directMounts,
    {
      packageResult: input.embeddedClaim.packageResult,
      packageObject: input.embeddedClaim.sourceObject,
      packageHierarchy: input.embeddedClaim.sourceHierarchy,
      shouldMergePackageAssets: false,
      isEmbeddedOccurrenceClaim: true,
      runtimeScopeNode: input.embeddedClaim.containerNode,
    },
  ]
}

/**
 * 将完整 GLB 包的源 occurrence 拓扑索引合并到项目运行时索引。
 *
 * hierarchy 重绑定已经把源对象 metadata 改为最终 runtime assemblyNodeId；这里通过
 * importer 保留的 source scene binding 精确读取映射，不根据名称或遍历顺序猜测。
 *
 * @param packageResult 当前完整 GLB 包结果。
 * @param target 项目组合结果的运行时拓扑索引。
 */
function mergeRebasedCompleteGlbTopologySources(
  packageResult: ProjectModelPackageResult,
  target: Map<string, ImportedTopologySource>,
  definitionTarget: Map<string, string>,
): void {
  if (packageResult.result.kind !== 'composed-assembly') {
    return
  }
  const sourceByAssemblyNodeId = packageResult.result.importedTopologySourceByAssemblyNodeId
  const definitionByAssemblyNodeId = packageResult.result.definitionNodeIdByAssemblyNodeId
  if (
    (!sourceByAssemblyNodeId || sourceByAssemblyNodeId.size === 0)
    && (!definitionByAssemblyNodeId || definitionByAssemblyNodeId.size === 0)
  ) {
    return
  }

  const runtimeAssemblyNodeIdBySourceAssemblyNodeId = new Map<string, string>()
  const sourceAssemblyNodeIds = new Set<string>([
    ...(sourceByAssemblyNodeId?.keys() ?? []),
    ...(definitionByAssemblyNodeId?.keys() ?? []),
  ])
  sourceAssemblyNodeIds.forEach((sourceAssemblyNodeId) => {
    const sourceObjectIds = packageResult.result.kind === 'composed-assembly'
      ? packageResult.result.sceneBindings.byAssemblyNodeId[sourceAssemblyNodeId] ?? []
      : []
    const runtimeAssemblyNodeIds = new Set(
      sourceObjectIds
        .map((objectId) => packageResult.result.kind === 'composed-assembly'
          ? packageResult.result.sceneBindings.byObject3DUuid[objectId]
          : undefined)
        .map((object) => object
          ? ImportedModelObjectMetadataService.resolveAssemblyNodeId(object)?.trim()
          : undefined)
        .filter((assemblyNodeId): assemblyNodeId is string => Boolean(assemblyNodeId)),
    )
    if (runtimeAssemblyNodeIds.size !== 1) {
      throw new Error(
        `完整 GLB occurrence 无法唯一映射到项目运行时身份：source=${sourceAssemblyNodeId}, candidates=${Array.from(runtimeAssemblyNodeIds).join(',') || 'none'}`,
      )
    }
    runtimeAssemblyNodeIdBySourceAssemblyNodeId.set(
      sourceAssemblyNodeId,
      Array.from(runtimeAssemblyNodeIds)[0],
    )
  })

  if (sourceByAssemblyNodeId && sourceByAssemblyNodeId.size > 0) {
    const rebased = rebaseProjectModelImportedTopologySources({
      sourceByAssemblyNodeId,
      runtimeAssemblyNodeIdBySourceAssemblyNodeId,
    })
    rebased.forEach((topologySource, runtimeAssemblyNodeId) => {
      if (target.has(runtimeAssemblyNodeId)) {
        throw new Error(`项目运行时拓扑 occurrence 身份冲突：${runtimeAssemblyNodeId}`)
      }
      target.set(runtimeAssemblyNodeId, topologySource)
    })
  }
  definitionByAssemblyNodeId?.forEach((definitionNodeId, sourceAssemblyNodeId) => {
    const runtimeAssemblyNodeId = runtimeAssemblyNodeIdBySourceAssemblyNodeId.get(sourceAssemblyNodeId)
    if (!runtimeAssemblyNodeId) {
      throw new Error(`完整 GLB definition 缺少运行时 occurrence 映射：source=${sourceAssemblyNodeId}`)
    }
    const existingDefinitionNodeId = definitionTarget.get(runtimeAssemblyNodeId)
    if (existingDefinitionNodeId && existingDefinitionNodeId !== definitionNodeId) {
      throw new Error(
        `项目运行时 definition 身份冲突：runtime=${runtimeAssemblyNodeId}, definitions=${existingDefinitionNodeId},${definitionNodeId}`,
      )
    }
    definitionTarget.set(runtimeAssemblyNodeId, definitionNodeId)
  })
}

/**
 * 创建项目模型节点容器并递归挂载子节点和包结果。
 *
 * @param input 递归构建输入。
 * @returns 当前节点的 hierarchy。
 */
function buildProjectModelNodeObject(input: {
  readonly node: ModelAssetNode
  readonly parentGroup: THREE.Group | null
  readonly parentObjectId: string | null
  readonly parentPath: string
  readonly runtimeAssemblyNodeIds: ReadonlyMap<ModelAssetNode, string>
  readonly packageResultsByNodeKey: ReadonlyMap<string, readonly ProjectModelPackageResult[]>
  readonly embeddedClaimsByNode: ReadonlyMap<ModelAssetNode, EmbeddedPuppyOccurrenceClaim>
  readonly sceneBindings: SceneBindingIndex
  readonly selectionIndex: SelectionIndex
  readonly partAssetsByUuid: Record<string, PartAsset>
  readonly assemblyAssetsByUuid: Record<string, AssemblyModelAsset>
  readonly importedTopologySourceByAssemblyNodeId: Map<string, ImportedTopologySource>
  readonly definitionNodeIdByAssemblyNodeId: Map<string, string>
  readonly warnings: string[]
}): GlbHierarchyNode {
  const nodeName = resolveNodeDisplayName(input.node)
  const runtimeAssemblyNodeId = resolveProjectModelRuntimeAssemblyNodeId(
    input.node,
    input.runtimeAssemblyNodeIds,
  )
  const nodeGroup = new THREE.Group()
  nodeGroup.name = input.parentGroup ? nodeName : IMPORTED_ASSEMBLY_ROOT_NAME
  applyOpenProjectRowMajorTransform(nodeGroup, input.node.localTransform)
  registerSceneBinding(input.sceneBindings, input.node.nodeKey, runtimeAssemblyNodeId, nodeGroup)
  input.selectionIndex.byObject3DUuid[nodeGroup.uuid] = {
    assemblyNodeId: runtimeAssemblyNodeId,
  }

  if (input.parentGroup) {
    input.parentGroup.add(nodeGroup)
  }

  const parentPath = input.parentPath
  const path = parentPath ? `${parentPath}/${nodeName}` : nodeName
  const packageHierarchyChildren: GlbHierarchyNode[] = []
  const packageSemanticMetas: ProjectNodePackageSemanticMeta[] = []
  let hasDirectPackageGeometry = false
  const packageMounts = createProjectModelPackageMounts({
    ownerNode: input.node,
    packageResults: input.packageResultsByNodeKey.get(input.node.nodeKey) ?? [],
    embeddedClaim: input.embeddedClaimsByNode.get(input.node),
  })
  for (const packageMount of packageMounts) {
    const packageResult = packageMount.packageResult
    const packageObject = packageMount.packageObject
    if (packageMount.shouldMergePackageAssets) {
      mergeImportedResultAssets(
        packageResult.result,
        input.partAssetsByUuid,
        input.assemblyAssetsByUuid,
      )
      if (packageResult.result.kind === 'composed-assembly') {
        input.warnings.push(...packageResult.result.warnings)
      }
    }

    const packageHierarchy = packageMount.packageHierarchy
    const packageMountAssemblyNodeId = resolveProjectModelRuntimeAssemblyNodeId(
      packageMount.runtimeScopeNode,
      input.runtimeAssemblyNodeIds,
    )
    if (packageMount.isEmbeddedOccurrenceClaim) {
      // 零件折叠只会递归改写 Body；被认领的组件根仍必须先绑定到项目 occurrence，
      // 否则视口右键会继续携带父容器作用域的 assemblyNodeId。
      writeHierarchyMetadata(
        packageObject,
        packageResult.file.fileId,
        packageHierarchy,
        runtimeAssemblyNodeId,
      )
      assertProjectModelRuntimeAssemblyIdentity({
        nodeKey: input.node.nodeKey,
        object: packageObject,
        selectionIndex: input.selectionIndex,
        expectedAssemblyNodeId: runtimeAssemblyNodeId,
      })
    }
    hasDirectPackageGeometry = hasDirectPackageGeometry
      || Boolean(packageHierarchy.meta?.hasGeometry)
    const packageObjectByUuid = buildObjectIndex(packageObject)
    const foldedPackageChildren = rebaseFoldedPackageHierarchy({
      source: packageHierarchy,
      ownerNodeType: input.node.nodeType,
      parentObjectId: nodeGroup.uuid,
      parentPath: path,
      parentDepth: input.node.depth,
      fileId: packageResult.file.fileId,
      assemblyNodeId: runtimeAssemblyNodeId,
      packageMountAssemblyNodeId,
      runtimeRoot: packageObject,
      objectByUuid: packageObjectByUuid,
    })
    if (packageMount.shouldMergePackageAssets) {
      mergeRebasedCompleteGlbTopologySources(
        packageResult,
        input.importedTopologySourceByAssemblyNodeId,
        input.definitionNodeIdByAssemblyNodeId,
      )
    }
    const hierarchyBodyOccurrenceRebindings = createImportedModelTopologyOccurrenceRebindings({
      sourceRoots: [packageHierarchy],
      targetRoots: foldedPackageChildren,
    })
    const bodyOccurrenceRebindings = input.node.nodeType === 'part'
      ? completeImportedModelTopologySingleOccurrenceRebindings({
          root: packageObject,
          targetAssemblyNodeId: runtimeAssemblyNodeId,
          existingRebindings: hierarchyBodyOccurrenceRebindings,
        })
      : completeProjectModelAssemblyTopologyBodyOccurrenceRebindings({
          rootObject: packageObject,
          rebasedHierarchies: foldedPackageChildren,
          targetAssemblyNodeId: runtimeAssemblyNodeId,
          existingRebindings: hierarchyBodyOccurrenceRebindings,
        })
    /**
     * hierarchy 已给出包内到项目 occurrence 的完整映射。所有权转移入口掌握本次真实迁移的
     * visible mesh records，因此必须在改父节点前同步完成身份重绑定，不能转移后再按父链找回范围。
     */
    transferImportedModelTopologySubtreeOwnership({
      sourceRoot: packageObject,
      targetRoot: nodeGroup,
      bodyOccurrenceRebindings,
    })
    packageObject.removeFromParent()
    if (packageMount.isEmbeddedOccurrenceClaim) {
      resetClaimedPackageOccurrenceTransform(packageObject)
    }
    nodeGroup.add(packageObject)
    registerSceneBinding(input.sceneBindings, input.node.nodeKey, runtimeAssemblyNodeId, packageObject)
    const projectChildIdentitySets = createProjectChildIdentityLookupSetsForPackage({
      node: input.node,
      packageFile: packageResult.file,
    })
    const retainedPackageChildren = filterPackageHierarchyChildrenRepresentedByProjectChildren({
      foldedPackageChildren,
      projectChildIdentitySets,
    })
    const resourceProjectChildIdentitySets = createProjectChildIdentityLookupSetsForPackage({
      node: input.node,
      packageFile: packageResult.file,
      childFilter: (child) => child.assetSource?.kind !== 'embedded-puppy-occurrence',
    })
    const resourceRetainedPackageChildren = filterPackageHierarchyChildrenRepresentedByProjectChildren({
      foldedPackageChildren,
      projectChildIdentitySets: resourceProjectChildIdentitySets,
    })
    markPackageHierarchyChildrenRepresentedByProjectChildren({
      foldedPackageChildren,
      retainedPackageChildren: resourceRetainedPackageChildren,
      objectByUuid: packageObjectByUuid,
      projectChildIdentitySets: resourceProjectChildIdentitySets,
      packageObject,
    })
    packageHierarchyChildren.push(...retainedPackageChildren)
    const packageSemanticMeta = collectFoldedPackageSemanticMeta(
      packageHierarchy,
      retainedPackageChildren,
      runtimeAssemblyNodeId,
    )
    if (packageSemanticMeta) {
      packageSemanticMetas.push(packageSemanticMeta)
    }
  }

  const childHierarchies = input.node.children.map((child) =>
    buildProjectModelNodeObject({
      ...input,
      node: child,
      parentGroup: nodeGroup,
      parentObjectId: nodeGroup.uuid,
      parentPath: path,
    }))
  const allChildren = [...packageHierarchyChildren, ...childHierarchies]
  const hasDirectGeometry = hasDirectPackageGeometry
    || packageHierarchyChildren.some((child) => Boolean(child.meta?.hasGeometry))
  const hasGeometry = hasDirectGeometry || allChildren.some((child) => Boolean(child.meta?.hasGeometry))
  const hierarchy: GlbHierarchyNode = {
    objectId: nodeGroup.uuid,
    parentObjectId: input.parentObjectId,
    path,
    name: nodeName,
    nodeType: 'Group',
    selectable: hasGeometry,
    depth: input.node.depth,
    meta: createProjectNodeMeta(
      input.node,
      runtimeAssemblyNodeId,
      hasGeometry,
      hasDirectGeometry,
      mergeProjectNodePackageSemanticMetas(packageSemanticMetas),
    ),
    children: allChildren,
  }

  writeHierarchyMetadata(
    nodeGroup,
    resolveProjectNodeSemanticFileId(input.node),
    hierarchy,
    runtimeAssemblyNodeId,
  )
  /**
   * 项目节点 Group 是项目模型中的稳定组件边界。子节点可以接收位姿写入，
   * 根节点只作为文档容器；两者都以自身作为组件选择代理。
   */
  ImportedModelObjectMetadataService.writeMetadata(nodeGroup, {
    selectionProxyObjectId: nodeGroup.uuid,
    componentTransformRoot: Boolean(input.parentGroup),
  })
  assertProjectModelRuntimeAssemblyIdentity({
    nodeKey: input.node.nodeKey,
    object: nodeGroup,
    hierarchy,
    selectionIndex: input.selectionIndex,
    expectedAssemblyNodeId: runtimeAssemblyNodeId,
  })

  return hierarchy
}

/**
 * 将 OpenProject 根节点和节点包解析结果组合成单一可挂载模型。
 *
 * @param input 组合输入。
 * @returns imported-model 场景挂载链路可直接消费的组合装配结果。
 * @throws 当项目模型根场景对象无法完成组合时。
 */
export function composeProjectModelImportedResult(
  input: ComposeProjectModelImportedResultInput,
): ComposedAssemblyResult {
  const packageResults = normalizePackageResults(input)
  const packageResultsByNodeKey = groupPackageResultsByNodeKey(packageResults)
  // 运行身份只依赖项目树，应先于包内对象认领完成，确保容器身份错误不会被几何包状态掩盖。
  const runtimeAssemblyNodeIds = createProjectModelRuntimeAssemblyNodeIds(input.root)
  const embeddedClaimsByNode = createEmbeddedPuppyOccurrenceClaims({
    root: input.root,
    packageResults,
  })
  const sceneBindings: SceneBindingIndex = {
    byAssemblyNodeId: {},
    byProjectNodeKey: {},
    byObject3DUuid: {},
  }
  const selectionIndex: SelectionIndex = { byObject3DUuid: {} }
  const partAssetsByUuid: Record<string, PartAsset> = {}
  const assemblyAssetsByUuid: Record<string, AssemblyModelAsset> = {}
  const importedTopologySourceByAssemblyNodeId = new Map<string, ImportedTopologySource>()
  const definitionNodeIdByAssemblyNodeId = new Map<string, string>()
  const warnings: string[] = []
  const hierarchy = buildProjectModelNodeObject({
    node: input.root,
    parentGroup: null,
    parentObjectId: null,
    parentPath: '',
    runtimeAssemblyNodeIds,
    packageResultsByNodeKey,
    embeddedClaimsByNode,
    sceneBindings,
    selectionIndex,
    partAssetsByUuid,
    assemblyAssetsByUuid,
    importedTopologySourceByAssemblyNodeId,
    definitionNodeIdByAssemblyNodeId,
    warnings,
  })
  const rootObject = sceneBindings.byObject3DUuid[hierarchy.objectId] as THREE.Group | undefined
  if (!rootObject) {
    throw new Error(`项目模型根对象组合失败：nodeKey=${input.root.nodeKey}`)
  }
  const partName = input.partName?.trim() || resolveNodeDisplayName(input.root)
  const packageResourceScopes = packageResults.flatMap((packageResult) => (
    packageResult.result.kind === 'composed-assembly' && packageResult.result.resourceScope
      ? [packageResult.result.resourceScope]
      : []
  ))

  return {
    kind: 'composed-assembly',
    fileInfo: input.fileInfo ?? createProjectModelRootFileInfo(input.root),
    partName,
    root: rootObject,
    hierarchy: {
      ...hierarchy,
      name: partName,
      path: partName,
    },
    partAssetsByUuid,
    assemblyAssetsByUuid,
    manifest: buildProjectModelManifest(input.root, runtimeAssemblyNodeIds),
    selectionIndex,
    sceneBindings,
    warnings,
    sourceAssetKey: input.root.versionKey,
    importedTopologySourceByAssemblyNodeId: importedTopologySourceByAssemblyNodeId.size > 0
      ? importedTopologySourceByAssemblyNodeId
      : undefined,
    definitionNodeIdByAssemblyNodeId: definitionNodeIdByAssemblyNodeId.size > 0
      ? definitionNodeIdByAssemblyNodeId
      : undefined,
    persistenceStatus: packageResults.some((packageResult) => (
      packageResult.result.persistenceStatus === 'runtime-only'
    ))
      ? 'runtime-only'
      : undefined,
    resourceScope: packageResourceScopes.length > 0
      ? ImportedModelResourceScope.retainAll(packageResourceScopes)
      : undefined,
  }
}
